import { ProgressBar, Form } from 'react-bootstrap';
import Badge from '../../base/Badge';

import { Button } from 'react-bootstrap';
import RevealDropdown, {
  RevealDropdownTrigger
} from '../../base/RevealDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatProductLabel } from '../../../helpers/productLabel';
import {
  faEdit,
  faTrash,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import {
  CABLE_COLOR_HEX,
  productColorBackground
} from '../../../helpers/productColor';

// helper
const fmt = n => Number(n || 0).toLocaleString();

// Stock lookup key — full label + colour, matching the warehouse item's cableName
// ("CYYF 3x2.5") + colour. Used to show available stock per product.
export const stockKey = p =>
  `${p?.cableName || ''} ${p?.numberOfConductors || ''}x${p?.section || ''}`
    .trim()
    .toUpperCase() +
  '|' +
  String(p?.color || '').toUpperCase();
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

// readable black/white text on a colour swatch (same idea as the create wizard)
const textOn = colorName => {
  const hex = CABLE_COLOR_HEX[String(colorName || '').trim().toUpperCase()];
  if (!hex || hex.length < 7) return '#fff';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#111' : '#fff';
};

const planMeta = p => {
  const total = Number(p?.totalOrderedLength) || 0;
  const planned = Number(p?.deliveryPlan?.plannedTotal) || 0;

  let planStatus = 'missing';
  if (total > 0 && planned === total) planStatus = 'planned';
  else if (total > 0 && planned > 0 && planned < total) planStatus = 'partial';
  else if (total > 0 && planned > total) planStatus = 'partial'; // over -> tot partial (sau faci "over" dacă vrei)

  const pct = total > 0 ? clamp((planned / total) * 100, 0, 100) : 0;

  // styling
  let badge = { label: 'Missing', type: 'secondary' };
  if (planStatus === 'planned') badge = { label: 'Planned', type: 'success' };
  if (planStatus === 'partial') badge = { label: 'Partial', type: 'warning' };

  // progress bar variant
  let progressVariant = 'secondary';
  if (planStatus === 'planned') progressVariant = 'success';
  if (planStatus === 'partial') progressVariant = 'warning';
  if (planned > total && total > 0) progressVariant = 'danger';

  return { total, planned, planStatus, pct, badge, progressVariant };
};

const productLabel = p => (p ? formatProductLabel(p) : '');

// dropdown items pentru Plan/Edit/Delete
const ProductActionsDropdown = ({ onPlan, onEdit, onDelete }) => {
  return (
    <RevealDropdownTrigger>
      <RevealDropdown popperConfig={{ strategy: 'fixed' }}>
        <div className="d-flex align-items-center gap-3 px-3 py-2">
          <Button size="sm" variant="outline-primary" onClick={onPlan}>
            <FontAwesomeIcon icon={faClipboardList} className="me-1" />
            Plan
          </Button>

          <Button size="sm" variant="outline-warning" onClick={onEdit}>
            <FontAwesomeIcon icon={faEdit} className="me-1" />
            Edit
          </Button>

          <Button size="sm" variant="outline-danger" onClick={onDelete}>
            <FontAwesomeIcon icon={faTrash} className="me-1" />
            Delete
          </Button>
        </div>
      </RevealDropdown>
    </RevealDropdownTrigger>
  );
};

export const makeOrderProductsColumns = ({
  openPlanModal,
  handleEditProduct,
  handleRemoveProduct,
  toggleFromStock,
  stockAvail = {}
}) => [
  {
    accessorKey: 'index',
    header: '#',
    cell: ({ row: { original } }) => (
      <span className="text-900 fw-semibold">{original.index}</span>
    ),
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '4%' } }
    }
  },

  {
    accessorKey: 'productLabel',
    header: 'Product',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column">
        <span className="fw-bold text-900">{original.productLabel}</span>
        <span className="text-muted fs-10">
          {original.hasMixedConductorSections
            ? `Mixed sections (GY ${original.greenYellowSection})`
            : 'Single section'}
        </span>
      </div>
    ),
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '10%' } }
    }
  },

  {
    accessorKey: 'color',
    header: 'Color',
    cell: ({ row: { original } }) => {
      if (!original.color)
        return (
          <Badge variant="phoenix" bg="secondary">
            —
          </Badge>
        );
      return (
        <span
          className="badge rounded-pill px-3 py-2 fw-semibold"
          style={{
            background: productColorBackground(original.color),
            color: textOn(original.color),
            border: '1px solid rgba(0,0,0,0.15)'
          }}
        >
          {String(original.color).toUpperCase()}
        </span>
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '10%' } }
    }
  },

  {
    accessorKey: 'totalOrderedLength',
    header: 'Length (m)',
    cell: ({ row: { original } }) => (
      <span className="fw-semibold text-900">
        {fmt(original.totalOrderedLength)}
      </span>
    ),
    meta: {
      cellProps: { className: 'py-3 white-space-nowrap' },
      headerProps: { style: { width: '12%' } }
    }
  },

  {
    id: 'fromStock',
    header: 'From stock',
    cell: ({ row: { original } }) => {
      const avail = Number(stockAvail[stockKey(original)] || 0);
      const want = Number(original.totalOrderedLength) || 0;
      const on = !!original.fromStock;
      const cover = avail >= want && want > 0 ? 'full' : avail > 0 ? 'partial' : 'none';
      return (
        <div className="d-flex flex-column gap-1">
          <Form.Check
            type="switch"
            id={`fromstock-${original.__rowIndex}`}
            checked={on}
            onChange={() => toggleFromStock?.(original.__rowIndex)}
            label={<span className="fs-10">{on ? 'Din stoc' : 'Producție'}</span>}
          />
          <span
            className={`fs-10 ${
              cover === 'full'
                ? 'text-success'
                : cover === 'partial'
                  ? 'text-warning'
                  : 'text-body-tertiary'
            }`}
          >
            {avail > 0 ? `${fmt(avail)} m în stoc` : 'fără stoc'}
            {on && want > avail ? ` · ${fmt(Math.max(0, want - avail))} m de produs` : ''}
          </span>
        </div>
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '14%' } }
    }
  },

  {
    id: 'deliveryPlan',
    header: 'Delivery Plan',
    accessorFn: row => row.planStatus, // pt filtre
    cell: ({ row: { original } }) => {
      const m = planMeta(original);

      return (
        <div>
          <div className="d-flex align-items-center gap-2">
            <Badge variant="phoenix" bg={m.badge.type}>
              {m.badge.label}
            </Badge>

            <span className="text-muted fs-10">
              {fmt(m.planned)} / {fmt(m.total)} m
            </span>
          </div>

          <div className="mt-2">
            <ProgressBar
              now={m.pct}
              variant={m.progressVariant}
              style={{ height: 6 }}
            />
          </div>
        </div>
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '18%' } }
    }
  },

  {
    accessorKey: 'summary',
    header: 'Summary',
    cell: ({ row: { original } }) => {
      const text = original.deliveryPlan?.summary || '';
      const m = planMeta(original);

      return (
        <div style={{ maxWidth: 360 }}>
          <div className="text-truncate fw-semibold" title={text}>
            {text || <span className="text-muted">—</span>}
          </div>

          {/* mic status vizual extra */}
          <div className="d-flex align-items-center gap-2 mt-1">
            <span className="text-muted fs-10">{m.badge.label}</span>
            <span className="text-muted fs-10">
              {m.total > 0 ? `${Math.round(m.pct)}%` : '0%'}
            </span>
          </div>
        </div>
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '21%' } }
    }
  },

  {
    id: 'action',
    header: '',
    cell: ({ row: { original } }) => (
      <div className="d-flex justify-content-end">
        <ProductActionsDropdown
          onPlan={() => openPlanModal(original.__rowIndex)}
          onEdit={() => handleEditProduct(original.__rowIndex)}
          onDelete={() => handleRemoveProduct(original.__rowIndex)}
        />
      </div>
    ),
    meta: {
      headerProps: {
        style: { width: '120px' }, // ⬅️ mai larg
        className: 'text-end'
      },
      cellProps: {
        className: 'text-end pe-3 white-space-nowrap'
      }
    }
  }
];

// adapter pentru tabel (din products -> row)
export const adaptProductToRow = (p, index) => {
  const m = planMeta(p);

  return {
    ...p,
    __rowIndex: index, // pentru callback-uri Plan/Edit/Delete
    index: index + 1,
    productLabel: productLabel(p),
    planStatus: m.planStatus,
    fromStock: !!p.fromStock
  };
};
